import type React from 'react';
import type { ChangeEvent } from 'react';
import { useEffect, useRef } from 'react';

import { CloseXIcon } from 'assets/svg-icons';
import type { ExpertState, IMessages } from 'components/product-expert/product-expert';
import { OTEventName } from 'const/oentok-event';
import useScreenSize from 'hooks/useScreenSize';
import { useTranslation } from 'react-i18next';

import classes from './text-chat.module.scss';

const TextMessage = ({ message, sessionId }: { message: IMessages; sessionId: string }) => {
  const isValidHttpUrl = (string: string) => {
    let url;
    try {
      url = new URL(string);
    } catch (_) {
      return false;
    }
    return url.protocol === 'http:' || url.protocol === 'https:';
  };

  return message.sessionId !== sessionId ? (
    <div className={classes.incomingChat}>
      <div className={classes.bubble}>
        <p>
          {isValidHttpUrl(message.message) ? (
            <a
              style={{ color: 'white' }}
              href={message.message}
              target={'_blank'}
              rel="noreferrer"
            >
              {message.message}
            </a>
          ) : (
            message.message
          )}
        </p>
      </div>
    </div>
  ) : (
    <div className={classes.outgoingChat}>
      <div className={classes.bubble}>
        <p>
          {isValidHttpUrl(message.message) ? (
            <a
              style={{ color: 'white' }}
              href={message.message}
              target={'_blank'}
              rel="noreferrer"
            >
              {message.message}
            </a>
          ) : (
            message.message
          )}
        </p>
      </div>
    </div>
  );
};

const TextChat = ({
  session,
  chatState,
  setChatState
}: {
  session: OT.Session;
  chatState: ExpertState;
  setChatState: (
    value: (prevState: ExpertState) => {
      mode: string;
      openView: string;
      messages: IMessages[];
      minimized: boolean;
      chatEnabled: boolean;
      message: string;
      newMessageCount: number;
    }
  ) => void;
}) => {
  const { isMobile } = useScreenSize();
  const chatContainer = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const scrollToBottom = () => {
    const div = chatContainer.current;
    if (!div) return;
    div.scrollTop = div.scrollHeight - div.clientHeight;
  };
  const closeModal = () => {
    setChatState((prevState: ExpertState) => ({
      ...prevState,
      chatEnabled: false
    }));
  };

  const setMessage = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setChatState((prevState) => ({ ...prevState, message: value }));
  };

  const ref = useRef<HTMLInputElement>(null);

  const onSubmitForm = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (chatState.message === '') {
      return;
    }

    session.signal(
      {
        data: JSON.stringify({
          message: chatState.message,
          sessionId: session.sessionId
        }),
        type: 'TEXT'
      },
      (e) => console.error(e)
    );

    setChatState((chatState) => ({
      ...chatState,
      message: ''
    }));
  };

  useEffect(() => {
    session.on(OTEventName.TEXT, function () {
      scrollToBottom();
    });
  }, []);

  return (
    <div
      style={chatState.mode === 'CHAT' ? { height: isMobile ? '40vh' : '75vh' } : {}}
      className={classes.cwTextMessageContainer}
    >
      <div className={classes.cwTextMessageHeader}>
        {/* Text:  */}
        <p>{t('textChat.talk_to_your_expert')}</p>
        {chatState.mode !== 'CHAT' && (
          <span
            className={classes.cwTextMessageHeaderClose}
            onClick={closeModal}
          >
            <span>
              <CloseXIcon />
            </span>
          </span>
        )}
      </div>
      <div
        ref={chatContainer}
        className={classes.cwTextMessageBody}
      >
        {chatState.messages.map((message, index: number) => (
          <TextMessage
            message={message}
            key={index}
            sessionId={session.sessionId}
          />
        ))}
      </div>
      <div className={classes.cwTextMessageFooter}>
        <form
          className={classes.cwTextMessageForm}
          onSubmit={onSubmitForm}
        >
          <input
            type="text"
            // Text; Type a message...
            placeholder={t('textChat.type_message')!}
            ref={ref}
            value={chatState.message}
            onChange={setMessage}
          />
          {/* Text: Send */}
          <button
            aria-label={`Send message button`}
            type="submit"
          >
            {t('common.send')}
          </button>
        </form>
      </div>
    </div>
  );
};

export default TextChat;
